import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { useMemo } from "react";

import { usePartnerConfig, usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { useActiveTask, useAgentStore, useCallbackState, useSession } from "@/sdk";

interface EndOfCallSummary {
    /** True when the end-of-call summary modal should be shown. */
    displaySummary: boolean;
    /** True to render the updated SessionSummary flow; false to fall back to DeprecatedSessionSummary. */
    showUpdatedFlow: boolean;
}

/**
 * End-of-call summary wiring, mirroring WorkspaceBody: on a customer-session wrapping state the summary
 * modal should open; submitting it clears the task so the call bar collapses.
 *
 * `customerSession` is `session.kind === "with-customer"` upstream (Workspace.tsx); it's passed in rather
 * than re-derived to avoid divergence.
 */
export function useEndOfCallSummary({ customerSession }: { customerSession: boolean }): EndOfCallSummary {
    const session = useSession();
    const callbackState = useCallbackState();
    const currentActivity = useAgentStore((state) => state.activity);
    const activeTask = useActiveTask();
    const { simplifiedSummary } = usePartnerFeatureConfigured();
    const { wrappingAgentActivity } = usePartnerConfig();
    const {
        features: { isUpdatedEndSessionFlowEnabled },
    } = useFeatures<WorkspaceFeatures>();
    const hasPendingCallback = callbackState?.callbackType === "CallbackNow";
    const isWrappingState =
        currentActivity === wrappingAgentActivity ||
        activeTask?.status === "wrapping" ||
        (customerSession && !!session.wrappingState);
    const displaySummary = customerSession && isWrappingState && !hasPendingCallback;
    const showUpdatedFlow = isUpdatedEndSessionFlowEnabled && !simplifiedSummary;

    return useMemo(() => ({ displaySummary, showUpdatedFlow }), [displaySummary, showUpdatedFlow]);
}
